import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Music, Video, ImageIcon, TrendingUp, Sparkles } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SongsStream from '../components/SongsStream';
import VideosStream from '../components/VideosStream';
import ImagesStream from '../components/ImagesStream';

const TABS = ['all', 'music', 'videos', 'images'];

const Discover = () => {
    const location = useLocation();
    // "View All" links elsewhere on the site (e.g. the community Songs/Videos
    // sections) land here pre-selected on a specific tab via ?tab=.
    const [activeTab, setActiveTab] = useState(() => {
        const urlTab = new URLSearchParams(location.search).get('tab');
        return TABS.includes(urlTab) ? urlTab : 'all';
    });

    const tabs = [
        { id: 'all', label: 'All Media', icon: <Sparkles className="w-4 h-4" /> },
        { id: 'music', label: 'Music', icon: <Music className="w-4 h-4" /> },
        { id: 'videos', label: 'Videos', icon: <Video className="w-4 h-4" /> },
        { id: 'images', label: 'Artwork', icon: <ImageIcon className="w-4 h-4" /> }
    ];

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader 
                title="Discover"
                subtitle="Explore the latest music, videos, and artwork from The Deep Wave community"
                icon={<TrendingUp className="w-8 h-8" />}
            />

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Tab Navigation */}
                <div className="flex flex-wrap gap-2 mb-8 p-1 bg-gray-800/50 rounded-lg border border-gray-700">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                                activeTab === tab.id
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content Based on Active Tab */}
                {activeTab === 'all' && (
                    <div className="space-y-12">
                        {/* Latest Music */}
                        <section>
                            <div className="flex items-center gap-3 mb-6">
                                <Music className="w-6 h-6 text-purple-400" />
                                <h2 className="text-2xl font-bold text-white">Latest Music</h2>
                            </div>
                            <SongsStream limit={6} showTitle={false} />
                        </section>

                        {/* Latest Videos */}
                        <section>
                            <div className="flex items-center gap-3 mb-6">
                                <Video className="w-6 h-6 text-pink-400" />
                                <h2 className="text-2xl font-bold text-white">Latest Videos</h2>
                            </div>
                            <VideosStream limit={6} showTitle={false} />
                        </section>

                        {/* Latest Artwork */}
                        <section>
                            <div className="flex items-center gap-3 mb-6">
                                <ImageIcon className="w-6 h-6 text-blue-400" />
                                <h2 className="text-2xl font-bold text-white">Latest Artwork</h2>
                            </div>
                            <ImagesStream limit={6} showTitle={false} />
                        </section>
                    </div>
                )}

                {activeTab === 'music' && (
                    <div className="space-y-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Music className="w-6 h-6 text-purple-400" />
                            <h2 className="text-2xl font-bold text-white">Discover Music</h2>
                        </div>
                        <SongsStream limit={12} showTitle={false} />
                    </div>
                )}

                {activeTab === 'videos' && (
                    <div className="space-y-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Video className="w-6 h-6 text-pink-400" />
                            <h2 className="text-2xl font-bold text-white">Discover Videos</h2>
                        </div>
                        <VideosStream limit={12} showTitle={false} />
                    </div>
                )}

                {activeTab === 'images' && (
                    <div className="space-y-8">
                        <div className="flex items-center gap-3 mb-6">
                            <ImageIcon className="w-6 h-6 text-blue-400" />
                            <h2 className="text-2xl font-bold text-white">Discover Artwork</h2>
                        </div>
                        <ImagesStream limit={12} showTitle={false} />
                        
                        {/* Link to Full Gallery */}
                        <div className="text-center mt-8">
                            <a
                                href="/gallery"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                <ImageIcon className="w-5 h-5" />
                                Browse Full Gallery
                            </a>
                        </div>
                    </div>
                )}

                {/* Featured Content Section (for all tabs) */}
                {activeTab === 'all' && (
                    <section className="mt-16">
                        <div className="text-center mb-12">
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <Sparkles className="w-6 h-6 text-yellow-400" />
                                <h2 className="text-3xl font-bold gradient-text">Featured Content</h2>
                                <Sparkles className="w-6 h-6 text-yellow-400" />
                            </div>
                            <p className="text-gray-400 max-w-2xl mx-auto">
                                Hand-picked highlights from our talented community members showcasing exceptional creativity and artistry.
                            </p>
                        </div>
                        
                        {/* Coming Soon for Featured Content */}
                        <div className="glass-card p-8 text-center border border-yellow-500/30">
                            <div className="text-4xl mb-4">✨</div>
                            <h3 className="text-xl font-bold text-yellow-400 mb-2">Featured Content Coming Soon</h3>
                            <p className="text-gray-400">
                                We're working on curating the best content from our community. Stay tuned for featured playlists, 
                                artist spotlights, and trending collections.
                            </p>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default Discover;